// 출결 탭 (매니저·관리자 공용, wide 레이아웃) — LMS식 구성:
// 긴급 알림 → 날짜별 현황(◀▶ 이동, 요약 타일·필터·명단 테이블) + 시간대별 현황(타임라인)
// → 센터 이용시간 관리(명단·엑셀·설정·학생 수정).
// 지난 날짜의 미등원(등록명단에 있는데 기록 없음)은 센터 이용시간 등록 기준으로
// 분류한다 — 클라이언트 확정(2026-07-19). 기록 조회 윈도는 fetcher의 최근 60일.
// 등·하원 시간표는 이용시간의 파생물이라 별도 편집 UI가 없다 — 이용시간 저장 시
// RPC가 자동 갱신한다 (scripts/add-center-hours.sql v2).
// 판정(지각/조퇴/결석)은 서버가 하고, 여기서는 결과 표시와 수동 정정만 한다.
// 매니저는 담당(배정) 학생만, 관리자(admin)는 전체 active 학생을 대상으로 한다.

import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Siren, X, MonitorSmartphone, FileSpreadsheet, CalendarOff,
  CalendarRange, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Clock3, ClipboardList,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext.jsx'
import { useData } from '../../context/DataContext.jsx'
import { supabase } from '../../lib/supabase.js'
import AttendanceExcelModal from '../../components/attendance/AttendanceExcelModal.jsx'
import ClosureManagerModal from '../../components/attendance/ClosureManagerModal.jsx'
import TodayAttendancePanel from '../../components/attendance/TodayAttendancePanel.jsx'
import TodayTimeline from '../../centerHours/TodayTimeline.jsx'
import CenterHoursSection from '../../centerHours/CenterHoursSection.jsx'
import { useCenterHours } from '../../centerHours/useCenterHours.js'
import {
  getDailyAttendanceBoard,
  getUnresolvedAttendanceNotifications,
  findClosureFor,
  dayLabel,
} from '../../context/selectors/attendance.js'
import { todayStr, toDateStr, daysAgoStr } from '../../utils/dateUtils.js'
import { isActiveStudent } from '../../data/studentStatus.js'

const STATUS_OPTIONS = [
  { value: 'present', label: '등원' },
  { value: 'late', label: '지각' },
  { value: 'absent', label: '결석' },
]

const NOTI_PREVIEW = 4 // 긴급 알림 접힘 상태에서 보여줄 개수

const hhmm = (iso) => (iso ? new Date(iso).toTimeString().slice(0, 5) : '')

// 'YYYY-MM-DD' → 'M/D' (휴무기간 배너 표시용)
const fmtMD = (dateStr) => {
  const [, m, d] = (dateStr ?? '').split('-').map(Number)
  return m && d ? `${m}/${d}` : dateStr
}

export default function AttendanceTab() {
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const isAdmin = currentUser?.role === 'admin'
  // 감독관(viewer)은 관리자와 같은 전체 범위를 열람하되 쓰기 액션은 숨긴다
  const isViewer = currentUser?.role === 'viewer'
  const seeAll = isAdmin || isViewer
  const {
    data, updateAttendance, createManualAttendance,
    resolveAttendanceNotification, resolveAllAttendanceNotifications,
    ingestAttendanceNotification,
  } = useData()
  const centerHours = useCenterHours()

  // Realtime — cron이 만든 긴급 알림을 열려 있는 화면에 즉시 반영
  useEffect(() => {
    const channel = supabase
      .channel('attendance-notifications')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'attendance_notifications' },
        (payload) => ingestAttendanceNotification(payload.new)
      )
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [ingestAttendanceNotification])

  const notifications = useMemo(
    () => getUnresolvedAttendanceNotifications(data, { educatorId: currentUser?.id, all: seeAll }),
    [data, currentUser?.id, seeAll]
  )

  // 조회 날짜 — 기본 오늘, ◀▶로 과거 이동 (기록 fetch 윈도인 60일까지)
  const [dateStr, setDateStr] = useState(todayStr)
  const isToday = dateStr === todayStr()
  const minDateStr = daysAgoStr(60)
  const shiftDate = (delta) => {
    const [y, m, d] = dateStr.split('-').map(Number)
    const next = toDateStr(new Date(y, m - 1, d + delta))
    if (next > todayStr() || next < minDateStr) return
    setDateStr(next)
  }

  // 예정 기준 = 센터 이용시간 등록명단. 아직 못 불러왔으면 시간표로 대체(null)
  const centerHoursReady =
    !centerHours.state.loading && !centerHours.state.error && !centerHours.state.migrationNeeded
  const board = useMemo(
    () => getDailyAttendanceBoard(data, {
      educatorId: currentUser?.id,
      all: seeAll,
      dateStr,
      registrations: centerHoursReady ? centerHours.registrations : null,
      closures: data.centerClosures,
    }),
    [data, currentUser?.id, seeAll, dateStr, centerHoursReady, centerHours.registrations]
  )

  // 조회 날짜가 속한 휴무기간 (없으면 null) — 배너 표시용
  const closure = useMemo(
    () => findClosureFor(data.centerClosures, dateStr),
    [data.centerClosures, dateStr]
  )

  const myStudents = useMemo(() => {
    if (seeAll) {
      return data.students
        .filter((s) => (s.status ?? 'active') === 'active')
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name, 'ko'))
    }
    const ids = new Set(
      data.assignments.filter((a) => a.educatorId === currentUser?.id).map((a) => a.studentId)
    )
    return data.students.filter((s) => ids.has(s.id) && (s.status ?? 'active') === 'active')
  }, [data.assignments, data.students, currentUser?.id, seeAll])

  // 이용시간 대리 수정 대상 — 재원 외(신청취소·퇴원)도 이름 검색으로 고칠 수 있어야
  // 한다 (2026-08 클라이언트). 매니저는 fetch 자체가 active만이라 사실상 관리자 범위.
  const centerHoursEditable = useMemo(() => {
    if (!seeAll) return myStudents
    return data.students
      .slice()
      .sort((a, b) =>
        Number(!isActiveStudent(a)) - Number(!isActiveStudent(b)) ||
        a.name.localeCompare(b.name, 'ko'))
  }, [data.students, myStudents, seeAll])

  const [editModal, setEditModal] = useState(null) // { student, record }
  // 긴급 알림 '확인' → 결석 사유 입력 (2026-08 클라이언트: 확인 시 출결 기록으로 연결)
  const [notiModal, setNotiModal] = useState(null) // { notification, student, record|null }

  const openNotiModal = (n) => {
    const student = data.students.find((s) => s.id === n.studentId)
      ?? { id: n.studentId, name: n.studentName || '학생' }
    const record = data.attendanceRecords.find(
      (r) => r.studentId === n.studentId && r.date === n.date
    ) ?? null
    setNotiModal({ notification: n, student, record })
  }

  // 사유 저장 → 기록 없으면 수동 결석 생성, 있으면 정정 → 알림 확인 처리
  const handleNotiSave = async (patch) => {
    const { notification, record } = notiModal
    if (record) {
      await updateAttendance(record.id, patch)
    } else {
      await createManualAttendance(notification.studentId, notification.date, patch)
    }
    await resolveAttendanceNotification(notification.id)
  }
  const [excelOpen, setExcelOpen] = useState(false)
  const [closureOpen, setClosureOpen] = useState(false)
  const [notiExpanded, setNotiExpanded] = useState(false)
  const [notiBusy, setNotiBusy] = useState(false)

  const [vy, vm, vd] = dateStr.split('-').map(Number)
  const viewDow = new Date(vy, vm - 1, vd).getDay()
  const visibleNotis = notiExpanded ? notifications : notifications.slice(0, NOTI_PREVIEW)

  const handleResolveAll = async () => {
    setNotiBusy(true)
    try {
      await resolveAllAttendanceNotifications(notifications.map((n) => n.id))
    } catch {
      // 실패는 전역 Toast가 표면화한다.
    } finally {
      setNotiBusy(false)
    }
  }

  return (
    <div className="py-6 space-y-8">
      {/* 긴급 알림 */}
      {notifications.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Siren size={16} className="text-red-500" />
            <span className="text-red-500 font-bold text-sm">긴급 확인 필요</span>
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              {notifications.length}
            </span>
            {!isViewer && (
              <button
                onClick={handleResolveAll}
                disabled={notiBusy}
                className="ml-auto px-3 py-1.5 bg-white border border-red-200 rounded-xl text-xs font-bold text-red-600 active:scale-95 transition-all disabled:opacity-40"
              >
                전체 확인 ({notifications.length}건)
              </button>
            )}
          </div>
          <div className="grid gap-2 lg:grid-cols-2">
            {visibleNotis.map((n) => (
              <div key={n.id} className="bg-red-50 border-2 border-red-300 rounded-2xl p-3.5 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-red-700 truncate">{n.message}</p>
                  <p className="text-[11px] text-red-400 mt-0.5">{n.date} · {hhmm(n.createdAt)}</p>
                </div>
                {!isViewer && (
                  <button
                    onClick={() => openNotiModal(n)}
                    className="flex-shrink-0 px-3 py-2 bg-white border border-red-200 rounded-xl text-xs font-bold text-red-600 active:scale-95 transition-all"
                  >
                    확인
                  </button>
                )}
              </div>
            ))}
          </div>
          {notifications.length > NOTI_PREVIEW && (
            <button
              onClick={() => setNotiExpanded((v) => !v)}
              className="w-full py-2 text-xs font-bold text-red-400 flex items-center justify-center gap-1"
            >
              {notiExpanded
                ? <>접기 <ChevronUp size={13} /></>
                : <>외 {notifications.length - NOTI_PREVIEW}건 더 보기 <ChevronDown size={13} /></>}
            </button>
          )}
        </div>
      )}

      {/* 헤더: 조회 날짜 이동(◀▶·달력·오늘 복귀) + 상단 액션 */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 mr-auto">
          <button
            onClick={() => shiftDate(-1)}
            disabled={dateStr <= minDateStr}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white active:scale-95 transition-all disabled:opacity-30"
            aria-label="이전 날짜"
          >
            <ChevronLeft size={18} />
          </button>
          <h2 className="text-lg font-bold text-gray-900 whitespace-nowrap">
            {vm}월 {vd}일 ({dayLabel(viewDow)}) 출결
          </h2>
          <button
            onClick={() => shiftDate(1)}
            disabled={isToday}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white active:scale-95 transition-all disabled:opacity-30"
            aria-label="다음 날짜"
          >
            <ChevronRight size={18} />
          </button>
          <input
            type="date"
            value={dateStr}
            min={minDateStr}
            max={todayStr()}
            onChange={(e) => { if (e.target.value) setDateStr(e.target.value) }}
            className="ml-1 border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-300"
            aria-label="조회 날짜 선택"
          />
          {!isToday && (
            <button
              onClick={() => setDateStr(todayStr())}
              className="ml-1 px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-bold active:scale-95 transition-all"
            >
              오늘
            </button>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={() => setClosureOpen(true)}
            className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-600 flex items-center gap-1.5 active:scale-95 transition-all"
          >
            <CalendarOff size={15} className="text-amber-500" />
            휴무기간 관리
          </button>
        )}
        <button
          onClick={() => navigate(isAdmin ? '/admin/staff-attendance' : '/manager/staff-attendance')}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-600 flex items-center gap-1.5 active:scale-95 transition-all"
        >
          <ClipboardList size={15} className="text-indigo-400" />
          강사 출근부
        </button>
        <button
          onClick={() => setExcelOpen(true)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-600 flex items-center gap-1.5 active:scale-95 transition-all"
        >
          <FileSpreadsheet size={15} className="text-emerald-500" />
          출결 엑셀
        </button>
        {!isViewer && (
          <button
            onClick={() => navigate(isAdmin ? '/admin/kiosk' : '/manager/kiosk')}
            className="px-4 py-2.5 bg-indigo-500 text-white rounded-xl text-sm font-bold flex items-center gap-1.5 active:scale-95 transition-all"
          >
            <MonitorSmartphone size={15} />
            키오스크 열기
          </button>
        )}
      </div>

      {/* 휴무기간 배너 — 조회 날짜가 휴무기간에 들면 안내 */}
      {closure && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 flex items-center gap-2">
          <CalendarOff size={16} className="flex-shrink-0 text-amber-500" />
          <p className="text-sm font-bold text-amber-700">
            휴무기간 — {closure.label || '휴무'} ({fmtMD(closure.startDate)}~{fmtMD(closure.endDate)})
            <span className="ml-1 font-medium text-amber-600">
              · 이 기간에는 자동 결석 판정이 중단됩니다
            </span>
          </p>
        </div>
      )}

      <AttendanceExcelModal open={excelOpen} onClose={() => setExcelOpen(false)} />
      {closureOpen && <ClosureManagerModal onClose={() => setClosureOpen(false)} />}

      {/* 날짜별 현황 (명단 테이블) + 시간대별 타임라인 */}
      <div className="grid gap-6 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2 space-y-2">
          {!isToday && (
            <p className="text-[11px] text-gray-400">
              지난 날짜 조회 — 미등원은 센터 이용시간 등록명단 기준(기록 없는 예정 학생)입니다.
            </p>
          )}
          <TodayAttendancePanel
            board={board}
            isToday={isToday}
            onEditRecord={isViewer ? null : (payload) => setEditModal(payload)}
          />
        </div>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Clock3 size={15} className="text-indigo-400" />
            <h3 className="text-sm font-bold text-gray-700">
              {isToday ? '오늘 시간대별 현황' : '시간대별 현황'}
            </h3>
          </div>
          {centerHours.state.loading ? (
            <CenterHoursLoading />
          ) : centerHours.state.migrationNeeded ? (
            <p className="bg-white rounded-2xl shadow-sm p-5 text-center text-sm text-gray-400">
              센터 이용시간 마이그레이션이 아직 적용되지 않았습니다.
            </p>
          ) : centerHours.state.error ? (
            <CenterHoursError onRetry={centerHours.reload} />
          ) : (
            <TodayTimeline
              registrations={centerHours.registrations}
              board={board}
              dateStr={dateStr}
              operatingDays={centerHours.config.operatingDays}
            />
          )}
        </div>
      </div>

      {/* 센터 이용시간 관리 */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <CalendarRange size={15} className="text-indigo-400" />
          <h3 className="text-sm font-bold text-gray-700">센터 이용시간 · 시간대별 명단</h3>
        </div>
        {centerHours.state.loading ? (
          <CenterHoursLoading />
        ) : centerHours.state.migrationNeeded ? (
          <p className="bg-white rounded-2xl shadow-sm p-5 text-center text-sm text-gray-400">
            센터 이용시간 마이그레이션(scripts/add-center-hours.sql)이 아직 적용되지 않았습니다.
          </p>
        ) : centerHours.state.error ? (
          <CenterHoursError onRetry={centerHours.reload} />
        ) : (
          <CenterHoursSection
            role={currentUser?.role}
            allStudents={data.students}
            editableStudents={centerHoursEditable}
            registrations={centerHours.registrations}
            config={centerHours.config}
            reload={centerHours.reload}
            readOnly={isViewer}
          />
        )}
      </div>

      {/* 수동 정정 모달 */}
      {editModal && (
        <CorrectionModal
          student={editModal.student}
          record={editModal.record}
          onClose={() => setEditModal(null)}
          onSave={(patch) => updateAttendance(editModal.record.id, patch)}
        />
      )}

      {/* 긴급 알림 확인 → 결석 사유 입력 (기록 없으면 수동 생성) */}
      {notiModal && (
        <CorrectionModal
          student={notiModal.student}
          record={notiModal.record}
          title={`${notiModal.student.name} 출결 확인 (${notiModal.notification.date})`}
          onClose={() => setNotiModal(null)}
          onSave={handleNotiSave}
          secondaryLabel="기록 없이 확인만"
          onSecondary={() => resolveAttendanceNotification(notiModal.notification.id)}
        />
      )}
    </div>
  )
}

function CenterHoursLoading() {
  return (
    <p className="bg-white rounded-2xl shadow-sm p-5 text-center text-sm text-gray-300">
      이용시간 데이터 불러오는 중...
    </p>
  )
}

// 센터 이용시간 데이터 로드 실패 안내 (마이그레이션 미적용과 구분)
function CenterHoursError({ onRetry }) {
  return (
    <p className="bg-white rounded-2xl shadow-sm p-5 text-center text-sm text-gray-400">
      센터 이용시간 데이터를 불러오지 못했습니다.
      <button onClick={onRetry} className="ml-2 text-indigo-500 font-bold">다시 시도</button>
    </p>
  )
}

// 출결 수동 정정 — 병결 처리 등 (source='manual'로 기록됨).
// record가 null이면 신규 수동 기록 폼(긴급 알림 확인 경로 — 기본 결석).
// onSave(patch): 저장 로직은 호출부가 바인딩. secondaryLabel/onSecondary는
// 선택적 보조 액션(예: 기록 없이 알림만 확인).
function CorrectionModal({ student, record = null, title, onClose, onSave, secondaryLabel, onSecondary }) {
  const [status, setStatus] = useState(record?.status ?? 'absent')
  const [note, setNote] = useState(record?.note ?? '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave({ status, note })
      onClose()
    } catch (e) {
      // 저장 실패는 전역 Toast가 표면화한다. (수동 생성 충돌 등 메시지가 있으면 노출)
      if (e?.message) alert(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleSecondary = async () => {
    setSaving(true)
    try {
      await onSecondary()
      onClose()
    } catch {
      // 실패는 전역 Toast가 표면화한다.
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center px-4 pb-4">
      <div className="bg-white rounded-3xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-gray-900 text-base">{title ?? `${student.name} 출결 정정`}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X size={20} />
          </button>
        </div>
        {!record && (
          <p className="text-xs text-gray-400 -mt-2">
            아직 출결 기록이 없는 학생입니다. 저장하면 <span className="font-bold">결석(수동)</span> 기록이
            생성되고 알림이 확인 처리됩니다. 등원은 키오스크로만 기록됩니다.
          </p>
        )}
        {/* 신규 생성 모드는 결석 고정 — 등원/지각은 키오스크 체크인 시각이 정본이라 수동 생성 불가 */}
        <div className={record ? 'flex gap-2' : 'hidden'}>
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setStatus(opt.value)}
              className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-all ${
                status === opt.value
                  ? 'border-indigo-400 bg-indigo-50 text-indigo-600'
                  : 'border-gray-100 text-gray-400'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="사유 메모 (예: 병결 확인)"
          rows={2}
          className="w-full border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-600 font-medium">
            취소
          </button>
          {secondaryLabel && onSecondary && (
            <button
              onClick={handleSecondary}
              disabled={saving}
              className="flex-1 py-3 border border-red-200 rounded-xl text-red-600 font-bold active:scale-95 transition-all disabled:opacity-40"
            >
              {secondaryLabel}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-3 bg-indigo-500 text-white rounded-xl font-bold active:scale-95 transition-all disabled:opacity-40"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  )
}
